import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type OnboardingStatus } from '../lib/api'
import { useAuth } from '../lib/auth'

// First-run checklist for a new company: share the form link and import past
// damages (per the workflow doc), plus inviting the team. Steps tick themselves
// off where we can tell, and the whole card can be dismissed for good.
export function GetStarted() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin'
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const [hidden, setHidden] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!isAdmin) return
    let active = true
    api
      .getOnboarding()
      .then((s) => active && setStatus(s))
      .catch(() => {
        /* non-admins / no scope: just don't show it */
      })
    return () => {
      active = false
    }
  }, [isAdmin])

  if (!isAdmin || !status || status.done || hidden) return null

  const formUrl = `${window.location.origin}/f/${status.formToken}`

  async function dismiss() {
    setHidden(true)
    await api.dismissOnboarding().catch(() => {})
  }

  return (
    <div className="card getstarted">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ fontWeight: 700, flex: 1 }}>Get started</div>
        <button className="link-btn" style={{ color: 'var(--gray)' }} onClick={() => void dismiss()}>
          Dismiss
        </button>
      </div>
      <p className="page-sub" style={{ marginBottom: 14 }}>
        Two quick things to set up your account.
      </p>

      <ol className="gs-steps">
        <li className="gs-step">
          <span className="gs-mark">1</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="gs-title">Share your damage-report form</div>
            <p className="gs-sub">Send this link to your field technicians — they don't need a login.</p>
            <div className="gs-actions">
              <input readOnly value={formUrl} onFocus={(e) => e.target.select()} className="gs-input" />
              <button
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(formUrl)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                  } catch {
                    /* clipboard blocked; the field is selectable */
                  }
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </li>

        <li className="gs-step">
          <span className={status.caseCount > 0 ? 'gs-mark done' : 'gs-mark'}>
            {status.caseCount > 0 ? '✓' : '2'}
          </span>
          <div style={{ flex: 1 }}>
            <div className="gs-title">Import your past damages</div>
            <p className="gs-sub">
              {status.caseCount > 0
                ? `${status.caseCount} case${status.caseCount === 1 ? '' : 's'} in the system.`
                : 'Upload an existing damage log (.xlsx or .csv) — you can review the column mapping first.'}
            </p>
            <div className="gs-actions">
              <Link className="btn" style={{ border: '1px solid var(--line)' }} to="/app/settings">
                Go to import
              </Link>
            </div>
          </div>
        </li>

        <li className="gs-step">
          <span className={status.userCount > 1 ? 'gs-mark done' : 'gs-mark'}>
            {status.userCount > 1 ? '✓' : '3'}
          </span>
          <div style={{ flex: 1 }}>
            <div className="gs-title">Invite your team <span style={{ color: 'var(--gray)', fontWeight: 400 }}>(optional)</span></div>
            <p className="gs-sub">
              {status.userCount > 1
                ? `${status.userCount} people have access.`
                : 'Add the office staff who will manage cases.'}
            </p>
            <div className="gs-actions">
              <Link className="btn" style={{ border: '1px solid var(--line)' }} to="/app/settings">
                Add users
              </Link>
            </div>
          </div>
        </li>
      </ol>
    </div>
  )
}
